import { useMemo } from "react";
import { useProfile } from "../../context/ProfileContext";
import { PERMISSIONS_CONFIG } from "../config/permissions-config";
import { PermissionRolesEnum } from "../enums/profiles";
import { isActionEnabled } from "../guards/ProfileGuard";
import { SinisterSector } from "../../pages/creditRecovery/constants/sectorOptions";

const ALL_SECTORS: SinisterSector[] = ["cobranca", "juridico"];

// O `group` das ações no PERMISSIONS_CONFIG é o que separa Cobrança de Jurídico
// (ex.: negotiateCollection tem group 'Cobrança', negotiateLegal tem 'Jurídico').
const SECTOR_BY_GROUP: Record<string, SinisterSector> = {
	Cobrança: "cobranca",
	Jurídico: "juridico",
};

/**
 * Setores que o perfil do usuário pode acessar em um módulo (Cobrança/Jurídico, Acordos).
 *
 * Deriva das permissões que já existem por setor, em vez de exigir uma permissão de
 * visualização nova: quem tem alguma ação `*Collection` atua em cobrança, quem tem alguma
 * `*Legal` atua no jurídico, e quem tem das duas vê as duas abas.
 *
 * Perfis somente-leitura (têm `View` do módulo mas nenhuma ação por setor) continuam vendo
 * as duas abas — esconder tudo deles seria regressão, já que hoje enxergam ambas.
 */
export const useAllowedSectors = (role: PermissionRolesEnum): SinisterSector[] => {
	const { permissions, isSuperAdmin, isPermissionInitialized } = useProfile();

	return useMemo(() => {
		if (!isPermissionInitialized || isSuperAdmin) return ALL_SECTORS;

		const rolePermissions = permissions?.[role];
		const moduleConfig = PERMISSIONS_CONFIG.find((module) => module.key === role);

		if (!rolePermissions || !moduleConfig) return ALL_SECTORS;

		const allowed = ALL_SECTORS.filter((sector) =>
			moduleConfig.actions.some(
				(action) => SECTOR_BY_GROUP[action.group ?? ""] === sector && isActionEnabled((rolePermissions as any)[action.action])
			)
		);

		return allowed.length > 0 ? allowed : ALL_SECTORS;
	}, [permissions, isSuperAdmin, isPermissionInitialized, role]);
};
